import React from "react";
import { Line } from "react-chartjs-2";
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
} from "chart.js";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";

// Register ChartJS components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
);

interface KYCExposureLineChartProps {
  kycData?: any; // Replace with proper type when API is defined
  isLoading?: boolean;
  error?: any;
  showTitle?: boolean;
}

const KYCExposureLineChart: React.FC<KYCExposureLineChartProps> = ({ 
  kycData,
  isLoading = false,
  error = null,
  showTitle = true
}) => {
  const theme = useTheme();

  // Sample data - Replace with actual API data when available
  const mockData = {
    labels: ['3', '4', '5', '6', '7', '8', '9', '10'],
    values: [3500000, 3800000, 10500000, 6800000, 7200000, 6200000, 10300000, 1800000]
  };
  
  const createChartData = () => {
    // If loading, don't try to create chart data yet
    if (isLoading) {
      return null;
    }
    
    // Use API data when available, fallback to mock data for now
    // When API is implemented, add proper data validation here, similar to other components
    const data = kycData || mockData;
    
    // Validate data structure before proceeding
    if (!data || !data.labels || !data.values || 
        !Array.isArray(data.labels) || !Array.isArray(data.values) || 
        data.labels.length === 0 || data.values.length === 0) {
      // Using mock data as a fallback if we're in development
      return {
        labels: mockData.labels,
        datasets: [
          {
            label: 'Total (Demo)',
            data: mockData.values,
            fill: false,
            backgroundColor: 'rgba(0, 48, 87, 0.2)',
            borderColor: '#003057',
            borderWidth: 3,
            pointRadius: 4,
            pointBorderWidth: 2,
            pointBackgroundColor: '#fff',
            tension: 0.3, // Smooth curve
          },
        ],
      };
    }
    
    return {
      labels: data.labels,
      datasets: [
        {
          label: 'Total',
          data: data.values,
          fill: false,
          backgroundColor: 'rgba(0, 48, 87, 0.2)',
          borderColor: '#003057',
          borderWidth: 3,
          pointRadius: 4,
          pointBorderWidth: 2,
          pointBackgroundColor: '#fff',
          tension: 0.3, // Smooth curve
        },
      ],
    };
  };

  const chartData = createChartData();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Typography color="error">Failed to load KYC exposure data</Typography>
      </Box>
    );
  }

  if (!chartData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px", flexDirection: "column", gap: 2 }}>
        <Typography color="text.secondary">No KYC data available</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
          Using demonstration data
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      {showTitle && (
        <Typography variant="h6" align="center" sx={{ mb: 2 }}>KYC - Exposure</Typography>
      )}
      <Box sx={{ height: showTitle ? "calc(100% - 50px)" : "100%" }}>
        <Line 
          data={chartData} 
          options={{ 
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: 'top',
                align: 'end',
                labels: {
                  usePointStyle: true,
                  boxWidth: 6,
                  color: theme.palette.text.primary,
                  font: {
                    weight: 'bold'
                  }
                }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                bodyFont: {
                  size: 14
                },
                callbacks: {
                  label: function (context) {
                    return `$${context.parsed.y.toLocaleString()}`;
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: {
                  color: theme.palette.divider,
                },
                ticks: {
                  callback: function (value) {
                    return `$${(Number(value) / 1000000).toFixed(1)}M`;
                  }
                }
              },
              x: {
                grid: {
                  display: false,
                }
              }
            },
            animation: {
              duration: 1000
            },
            interaction: {
              mode: 'index',
              intersect: false,
            },
            elements: {
              line: {
                borderJoinStyle: 'round'
              }
            }
          }} 
        />
      </Box>
    </Box>
  );
};

export default KYCExposureLineChart;
